import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, tokenStore } from './api'
import type { EntitlementSet, User } from './types'

interface LoginResponse {
  user: User
  tokens: { access_token: string; refresh_token: string; expires_at: string }
  entitlements: EntitlementSet
}

interface AuthContextValue {
  user: User | null
  entitlements: EntitlementSet | null
  ready: boolean
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, displayName: string) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = React.createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<User | null>(null)
  const [entitlements, setEntitlements] = React.useState<EntitlementSet | null>(null)
  const [ready, setReady] = React.useState(false)
  const queryClient = useQueryClient()

  const loadMe = React.useCallback(async () => {
    if (!tokenStore.get()) {
      setUser(null)
      setEntitlements(null)
      return
    }
    try {
      const me = await api.get<{ user: User; entitlements: EntitlementSet }>('/me')
      setUser(me.user)
      setEntitlements(me.entitlements)
    } catch {
      // An invalid or expired session simply means "logged out"; the API
      // client has already cleared the token.
      tokenStore.clear()
      setUser(null)
      setEntitlements(null)
    }
  }, [])

  React.useEffect(() => {
    void loadMe().finally(() => setReady(true))
  }, [loadMe])

  const value = React.useMemo<AuthContextValue>(
    () => ({
      user,
      entitlements,
      ready,
      async login(email, password) {
        const res = await api.post<LoginResponse>('/auth/login', { email, password })
        tokenStore.set(res.tokens.access_token, res.tokens.refresh_token)
        setUser(res.user)
        setEntitlements(res.entitlements)
        queryClient.clear()
      },
      async register(email, password, displayName) {
        await api.post('/auth/register', {
          email, password, display_name: displayName,
        })
      },
      async logout() {
        const refresh = tokenStore.getRefresh()
        try {
          if (refresh) await api.post('/auth/logout', { refresh_token: refresh })
        } finally {
          // Clearing the cache on logout is not optional: another user on the
          // same browser must never see the previous user's cached rules.
          tokenStore.clear()
          setUser(null)
          setEntitlements(null)
          queryClient.clear()
        }
      },
      refreshUser: loadMe,
    }),
    [user, entitlements, ready, loadMe, queryClient],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
